import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  useToast,
} from '@chakra-ui/react';
import { IconPlusLarge } from 'central-icons/IconPlusLarge';
import { ascending } from 'd3-array';
import { difference } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useUserTrialDetails } from '@revelio/auth';
import {
  BuildADashTrackingEvents,
  Loading,
  PagePaths,
  startCasePreserveChars,
} from '@revelio/core';

import {
  CardListSelect,
  CardListSelectController,
  type ColumnSet,
} from '../../../../shared/ui/card-list';
import { useUnleashFlags } from '../../../../shared/unleash-flags/use-unleash-flag';
import type { ReportPageConfig } from '../../../CustomDash/components/PageConfigProvider/types';
import { usePageManager } from '../../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { buildPathFromEntityType } from '../../../CustomDash/utils/routing-helpers';
import { DeliverableStepContainer } from '../../../deliverables/deliverable-step-container';
import TrialUpgradeLink from '../../../trialUser/TrialUpgradeLink';
import { useReportBuilderConfig } from '../../hooks/useReportBuilderConfig';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../../utils/report-builder-paths';
import {
  getPromotedSectionCardsForCategory,
  getPromotedTemplateForSelectionReportType,
  getVirtualMiddlewareReportSelection,
  isPromotedTemplateSelection,
} from '../../utils/report-template-promotions';
import { CustomReportBuilderContext } from '../custom-report-builder-provider';
import {
  reportBuilderStore,
  useAllReports,
} from '../report-builder.repository';
import { ReportBuilderDeliverablePaths } from '../report-builder-page';
import { CustomReportList } from './custom-report-list';
import { ReportSampleLink } from './report-sample-link';
import {
  createNewReportBuilderEntity,
  getReportBuilderReportIndex,
} from './report-type.model';

export const REPORT_TYPE_STEP_ID = 'report-type';

export const ReportType = () => {
  const { isTrialUser } = useUserTrialDetails();
  const navigate = useNavigate();
  const {
    selectedReportTemplate: selectedCustomReportTemplate,
    setReportTemplate,
    customReportsIsEnabled,
    experimentalReportsIsEnabled,
    experimentalReportTemplates,
  } = useContext(CustomReportBuilderContext);
  const { createPageDraft } = usePageManager();

  const reportBuilderDeliverables = useAllReports();

  const selectedReportType = reportBuilderDeliverables.map(
    (deliverable) => deliverable.reportType
  );

  const { config, categories, loading } = useReportBuilderConfig();
  const allFlags = useUnleashFlags();

  const columnSet: ColumnSet<string>[] = useMemo(() => {
    const base = categories.map((category) => ({
      heading:
        categories.length > 1 ? startCasePreserveChars(category, []) : null,
      columns: Object.entries(config || {})
        .filter(([_reportKey, reportConfig]) =>
          (reportConfig.categories ?? []).includes(category)
        )
        .map(([key, value]) => {
          const promoted = getPromotedTemplateForSelectionReportType(
            key,
            allFlags,
            customReportsIsEnabled
          );
          const link = (() => {
            if (promoted) {
              const filterGroups = promoted.sampleFilterGroups;
              if (filterGroups !== undefined) {
                return buildPathFromEntityType('report', {
                  templateId: promoted.id,
                  search: `?${new URLSearchParams({
                    filterGroups,
                  }).toString()}`,
                });
              }
              return buildPathFromEntityType('report', {
                templateId: promoted.id,
              });
            }
            if (value.sampleLink) {
              return `/${PagePaths.REPORTS_SAMPLE}?link=${encodeURIComponent(value.sampleLink)}`;
            }
            return null;
          })();
          const cta = (() => {
            if (link === null) {
              return <ReportSampleLink link={null} />;
            }
            if (promoted) {
              return (
                <Flex
                  alignItems="center"
                  gap={2}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ReportSampleLink link={link} />
                </Flex>
              );
            }
            return <ReportSampleLink link={link} />;
          })();

          return {
            id: key,
            label: value.label,
            detail: value.description,
            cta,
          };
        })
        .sort((a, b) => ascending(a.id, b.id)),
    }));

    if (!customReportsIsEnabled) {
      return base;
    }

    return base.map((set, index) => {
      const category = categories[index];
      if (category === undefined) {
        return set;
      }
      const promoted = getPromotedSectionCardsForCategory(
        category,
        allFlags,
        customReportsIsEnabled
      );
      if (promoted.length === 0) {
        return set;
      }
      return {
        ...set,
        columns: [
          ...promoted.map((col) => ({
            id: col.id,
            label: col.label,
            detail: col.detail,
            cta:
              col.sampleFilterGroups !== undefined ? (
                <Flex
                  alignItems="center"
                  gap={2}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ReportSampleLink
                    link={buildPathFromEntityType('report', {
                      templateId: col.templateId,
                      search: `?${new URLSearchParams({
                        filterGroups: col.sampleFilterGroups,
                      }).toString()}`,
                    })}
                  />
                </Flex>
              ) : undefined,
          })),
          ...set.columns,
        ],
      };
    });
  }, [categories, config, customReportsIsEnabled, allFlags]);

  const effectiveMiddlewareReportSelection =
    getVirtualMiddlewareReportSelection(
      selectedReportType,
      selectedCustomReportTemplate,
      allFlags,
      customReportsIsEnabled
    );

  const handleReportChange = (value: string[]) => {
    const newValuesAdded = difference(
      value,
      effectiveMiddlewareReportSelection
    );
    const newValuesRemoved = difference(
      effectiveMiddlewareReportSelection,
      value
    );

    if (newValuesRemoved.length) {
      const removedReportType = newValuesRemoved[0];
      if (removedReportType !== undefined) {
        const promotedForRemoved = getPromotedTemplateForSelectionReportType(
          removedReportType,
          allFlags,
          customReportsIsEnabled
        );
        if (
          promotedForRemoved &&
          selectedCustomReportTemplate?.id === promotedForRemoved.id
        ) {
          setReportTemplate(null);
        } else {
          const removeReportTypeId = getReportBuilderReportIndex(
            columnSet,
            removedReportType
          );
          reportBuilderStore.actions.removeReports([removeReportTypeId]);
        }
      }
    }

    if (newValuesAdded.length) {
      const selectedType = newValuesAdded[0];
      if (selectedType === undefined) {
        return;
      }

      const promotedTemplate = getPromotedTemplateForSelectionReportType(
        selectedType,
        allFlags,
        customReportsIsEnabled
      );
      if (promotedTemplate) {
        reportBuilderStore.actions.setReports([]);
        setReportTemplate(promotedTemplate);
        return;
      }

      setReportTemplate(null);

      const removeReportTypeIds = selectedReportType.map((reportType) =>
        getReportBuilderReportIndex(columnSet, reportType)
      );
      if (removeReportTypeIds.length) {
        reportBuilderStore.actions.removeReports(removeReportTypeIds);
      }

      const { newEntity } = createNewReportBuilderEntity(
        columnSet,
        selectedType
      );
      reportBuilderStore.actions.addReport(newEntity);
    }
  };

  const handleCustomReportSelect = (report: ReportPageConfig | null) => {
    if (report) {
      reportBuilderStore.actions.setReports([]);
      setReportTemplate(report);
      return;
    }
    setReportTemplate(null);
  };

  const submitValidation = (() => {
    const promotedSelection = isPromotedTemplateSelection(
      selectedCustomReportTemplate,
      allFlags,
      customReportsIsEnabled
    );
    const selectedAMiddlewareReport = selectedReportType.length === 1;
    const selectedACustomReport =
      !!selectedCustomReportTemplate && !promotedSelection;
    if (selectedAMiddlewareReport && selectedACustomReport) {
      return 'You can only select one report type to continue.';
    }

    const hasExactlyOne =
      (selectedReportType.length === 1 && !selectedCustomReportTemplate) ||
      (!!selectedCustomReportTemplate && selectedReportType.length === 0);

    if (!hasExactlyOne) {
      return 'You must select exactly one report type to continue.';
    }

    return undefined;
  })();

  const toast = useToast();
  const onSubmit = () => {
    if (toast.isActive('report-builder-trial-toast')) {
      return;
    }
    if (isTrialUser) {
      toast({
        id: 'report-builder-trial-toast',
        title: 'Upgrade Required',
        description: (
          <span>
            To customize reports,{' '}
            <TrialUpgradeLink toastIdClose="report-builder-trial-toast" />.
          </span>
        ),
        status: 'warning',
        variant: 'subtle',
        position: 'top-right',
        duration: null,
        isClosable: true,
      });
      return;
    }
    if (submitValidation) {
      toast({
        title: submitValidation,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    navigate(getReportBuilderPath(ReportBuilderStep.ENTITY));
  };

  const newReportTypeTitleElement = customReportsIsEnabled ? (
    <Button
      leftIcon={<IconPlusLarge size={16} />}
      size="sm"
      colorScheme="green"
      variant="ghost"
      onClick={() => {
        createPageDraft('report');
        mixpanel.track(BuildADashTrackingEvents.CREATE_NEW_PAGE, {
          entity_type: 'report',
        });
      }}
    >
      New Report Type
    </Button>
  ) : null;

  const showExperimentalSection =
    experimentalReportsIsEnabled && experimentalReportTemplates.length > 0;

  return (
    <DeliverableStepContainer
      disableFade
      id={REPORT_TYPE_STEP_ID}
      title="Select a Report Type"
      titleElement={newReportTypeTitleElement}
      onSubmit={onSubmit}
      startOverPath={`/${PagePaths.REPORT_BUILDER}/${ReportBuilderDeliverablePaths.REPORT_TYPE}`}
      isReportStep={true}
    >
      {loading ? (
        <Center h="100%">
          <Loading />
        </Center>
      ) : (
        <>
          <CustomReportList onReportTemplateChange={handleCustomReportSelect} />
          <CardListSelectController<string>
            isDisabled={!!isTrialUser}
            items={columnSet}
            value={effectiveMiddlewareReportSelection}
            onChange={handleReportChange}
            variant="report"
            gridProps={{
              spacing: '10px',
              columns: 2,
              gridAutoFlow: 'row',
            }}
          />
          {showExperimentalSection ? (
            <Box mb="8">
              <Flex align="center" justify="space-between" pb="3">
                <Heading
                  as="h3"
                  fontWeight="600"
                  size="sm"
                  color="text.primary"
                >
                  Experimental
                </Heading>
              </Flex>
              <SimpleGrid spacing="10px" columns={2} gridAutoFlow="row">
                {experimentalReportTemplates.map((report) => (
                  <CardListSelect<string>
                    key={report.id}
                    id={report.id}
                    label={report.name}
                    detail={report.tabs?.[0]?.label || ''}
                    variant="report"
                    isChecked={selectedCustomReportTemplate?.id === report.id}
                    onChange={() =>
                      handleCustomReportSelect(
                        selectedCustomReportTemplate?.id === report.id
                          ? null
                          : report
                      )
                    }
                    cta={
                      report.sampleFilterGroups ? (
                        <Flex
                          alignItems="center"
                          gap={2}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ReportSampleLink
                            link={buildPathFromEntityType('report', {
                              templateId: report.id,
                              search: `?${new URLSearchParams({
                                filterGroups: report.sampleFilterGroups,
                              }).toString()}`,
                            })}
                          />
                        </Flex>
                      ) : undefined
                    }
                  />
                ))}
              </SimpleGrid>
            </Box>
          ) : null}
        </>
      )}
    </DeliverableStepContainer>
  );
};
